import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Download, Upload, AlertCircle, CheckCircle2 } from 'lucide-react';

import { getLoggedInServiceNo } from '../../utils/masterDataUtils';
import PayrollBatchEmployeeService from '../../services/PayrollBatchEmployeeService';
import {
  downloadMultiBatchTemplate,
  readMultiBatchWorkbook,
  validateMultiBatchRow,
} from '../../utils/batchEmployeeImport';

export default function ImportBatchEmployeesModal({
  isOpen,
  onClose,
  batches = [],
  employees = [],
  onImportSuccess,
}) {
  const fileInputRef = useRef(null);

  const [rows, setRows] = useState([]);
  const [error, setError] = useState(null);
  const [importing, setImporting] = useState(false);
  const [loadingBatchData, setLoadingBatchData] = useState(false);
  const [existingBatchEmpMap, setExistingBatchEmpMap] = useState({});

  // Filter recurring batches
  const recurringBatches = useMemo(() => {
    return batches.filter(
      (b) => b.periodId === null || b.periodId === undefined || String(b.periodId).trim() === ''
    )
  }, [batches]);

  // Fetch active employees for all recurring batches whenever the modal opens
  useEffect(() => {
    if (!isOpen || !batches.length) {
      setExistingBatchEmpMap({});
      return;
    }

    let isMounted = true;
    setLoadingBatchData(true);

    Promise.all(
      batches.map(async (b) => {
        try {
          const res = await PayrollBatchEmployeeService.GetPayRollEmpByBatchId(b.id);
          const list = res?.data?.ResultSet || [];

          // Only get employees who are currently active and included
          const activeSet = new Set(
            list.filter(
              (i) => (i.HPBE_IS_ACTIVE === 'True' || i.HPBE_IS_ACTIVE === 1 || i.HPBE_IS_ACTIVE === true) &&
                  (i.HPBE_IS_INCLUDED === 'True' || i.HPBE_IS_INCLUDED === 1 || i.HPBE_IS_INCLUDED === true) 
            )
            .map((i) => String(i.HED_SERVICE_NO).trim().toLowerCase())
          );

          return {
            batchKey: (b.batchNo || b.code || '').trim().toLowerCase(),
            set: activeSet,
          }
        } catch (err) {
          console.error(`Failed to load employees for batch ${b.batchNo}`, err);
          return {
            batchKey: (b.batchNo || b.code || '').trim().toLowerCase(),
            set: new Set(),
          };
        }
      })
    ).then((results) => {
      if (!isMounted) return;
      const map = {};
      results.forEach(({ batchKey, set }) => {
        if (!batchKey) return;
        if (!map[batchKey]) {
          map[batchKey] = new Set(set);
        } else {
          // Merge sets if duplicate batch records exist
          set.forEach((emp) => map[batchKey].add(emp));
        }
      });
      setExistingBatchEmpMap(map);
      setLoadingBatchData(false);
    });

    return () => {
      isMounted = false;
    }
  }, [isOpen, batches]); 

  // Fast-lookup map for Master Employees
  const employeeMap = useMemo(() => {
    const map = {};
    employees.forEach((emp) => {
      const sno = (emp.HED_SERVICE_NO || emp.serviceNo || '').toLowerCase();
      if (sno) map[sno] = emp;
    });
    return map;
  }, [employees]);

  // Fast-lookup map for Batches by Batch Number
  const batchMapByNo = useMemo(() => {
    const map = {};
    batches.forEach((b) => {
      const bNo = (b.batchNo || b.code || '').trim().toLowerCase();
      if (bNo) map[bNo] = b;
    });
    return map;
  }, [batches]);

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(null);

    try {
      const parsed = await readMultiBatchWorkbook(file);
      if (!parsed.length) {
        setError('No data rows found in the selected file.');
        return;
      }

      // Check duplicate Service Nos in the file
      const countMap = {};
      parsed.forEach((r) => {
        const k = String(r.empNo || '').trim().toLowerCase();
        if (k) countMap[k] = (countMap[k] || 0) + 1;
      });
      const duplicateInFileSet = new Set(
        Object.keys(countMap).filter((k) => countMap[k] > 1)
      );

      const validated = parsed.map((r) =>
        validateMultiBatchRow(r, employeeMap, batchMapByNo, duplicateInFileSet, existingBatchEmpMap)
      );

      setRows(validated);
    } catch (err) {
      console.error(err);
      setError('Failed to read Excel file. Please use the downloaded template.');
    }
    e.target.value = '';
  };

  const validRows = useMemo(() => rows.filter((r) => r.errors.length === 0), [rows]);
  const invalidRows = useMemo(() => rows.filter((r) => r.errors.length > 0), [rows]);

  const uploadedBatchCols = useMemo(() => {
    if (!rows.length) return [];

    const colSet = new Set();
    rows.forEach((r) => {
      Object.keys(r.batchEntries || {}).forEach((col) => colSet.add(col));
    });

    return Array.from(colSet);
  }, [rows]);

  const handleImport = async () => {
    if (!validRows.length) return;
    setImporting(true);
    setError(null);

    const createdBy = getLoggedInServiceNo() || 'SYSTEM';
    let totalAssignments = 0;

    try {
      for (const row of validRows) {
        for (const assign of row.assignments) {
          await PayrollBatchEmployeeService.AddPayRollEmp({
            batchId: assign.batchId,
            employeeId: row.employeeId,
            amount: assign.amount,
            percentage: null,
            isIncluded: 1,
            isProcessed: 0,
            isActive: 1,
            overrideReason: '',
            remarks: 'Imported via Excel',
            createdBy,
          });
          totalAssignments++;
        }
      }

      if (onImportSuccess) {
        onImportSuccess(totalAssignments);
      }
      resetAndClose();
    } catch (err) {
      console.error(err);
      setError(err?.response?.data?.Message || err?.message || 'An error occurred during import.');
    } finally {
      setImporting(false);
    }
  };

  const resetAndClose = () => {
    setRows([]);
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
        onClick={!importing ? resetAndClose : undefined}
        aria-hidden="true"
      />

      {/* Modal Container */}
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-7xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center shrink-0">
          <h2 className="text-lg font-semibold text-gray-900">Import Batch Employees</h2>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => downloadMultiBatchTemplate(batches)}
              className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-primary-600 hover:bg-primary-50 rounded-md transition-colors"
            >
              <Download size={16} />
              Template
            </button>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={importing || loadingBatchData}
              className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Upload size={16} />
              {loadingBatchData ? 'Checking Batches...' : 'Upload File'}
            </button>
            <input
              type="file"
              accept=".xlsx,.xls"
              className="hidden"
              ref={fileInputRef}
              onChange={handleFileChange}
            />
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-4 space-y-4 overflow-y-auto flex-1">
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 text-red-700 text-sm rounded-md border border-red-100">
              <AlertCircle size={16} className="shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {rows.length > 0 ? (
            <div className="flex flex-col">
              <div className="flex justify-between items-center mb-3">
                <span className="text-sm font-medium text-gray-700">Preview Data</span>
                <div className="flex items-center gap-4 text-[13px]">
                  <span className="flex items-center gap-1.5 text-emerald-600 font-medium">
                    <CheckCircle2 size={15} /> {validRows.length} Valid
                  </span>
                  <span className="flex items-center gap-1.5 text-red-600 font-medium">
                    <AlertCircle size={15} /> {invalidRows.length} Invalid
                  </span>
                </div>
              </div>

              {/* Single scrollable, bordered container */}
              <div className="border border-gray-200 rounded-md overflow-auto max-h-[50vh]">
                <table className="w-full text-left text-xs whitespace-nowrap">
                  <thead className="bg-gray-50 border-b border-gray-200 sticky top-0 z-10">
                    <tr>
                      <th className="px-4 py-2.5 font-medium text-gray-600">Row</th>
                      <th className="px-4 py-2.5 font-medium text-gray-600">Service No</th>
                      <th className="px-4 py-2.5 font-medium text-gray-600">Employee Name</th>
                      
                      {/* Dynamic batch header columns */}
                      {uploadedBatchCols.map((colName) => {
                        const exists = Boolean(batchMapByNo[colName.toLowerCase()]);
                        return (
                          <th
                            key={colName}
                            className={`px-4 py-2.5 font-medium text-right ${
                              exists
                                ? 'text-gray-600'
                                : 'bg-red-100 text-red-700 border-x border-red-200'
                            }`}
                            title={!exists ? `Batch "${colName}" does not exist or is not recurring` : ''}
                          >
                            <div className="flex items-center justify-end gap-1">
                              <span>{colName}</span>
                              {!exists && <AlertCircle size={12} className="text-red-500 shrink-0" />}
                            </div>
                          </th>
                        );
                      })}

                      <th className="px-4 py-2.5 font-medium text-gray-600 min-w-[150px]">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {rows.map((r, i) => (
                      <tr
                        key={i}
                        className={r.errors.length ? 'bg-red-50/40' : 'hover:bg-gray-50/50'}
                      >
                        <td className="px-4 py-2 text-gray-500">{r._row}</td>
                        <td className="px-4 py-2 font-medium text-gray-900">{r.empNo}</td>
                        <td className="px-4 py-2 text-gray-700">{r.employeeName}</td>

                        {/* Dynamic batch value cells */}
                        {uploadedBatchCols.map((colName) => {
                          const exists = Boolean(batchMapByNo[colName.toLowerCase()]);
                          const rawVal = r.batchEntries?.[colName];
                          const hasVal = rawVal !== undefined && rawVal !== '';
                          const numVal = Number(String(rawVal).replace(/,/g, ''));

                          return (
                            <td
                              key={colName}
                              className={`px-4 py-2 text-right tabular-nums ${
                                !exists ? 'bg-red-50 text-red-700 border-x border-red-100' : 'text-gray-700'
                              }`}
                            >
                              {hasVal ? (
                                !Number.isNaN(numVal) ? (
                                  numVal.toLocaleString('en-US', {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })
                                ) : (
                                  <span className="text-red-600 font-semibold">{rawVal}</span>
                                )
                              ) : (
                                <span className="text-gray-300">—</span>
                              )}
                            </td>
                          );
                        })}

                        <td className="px-4 py-2">
                          {r.errors.length === 0 ? (
                            <span className="inline-flex items-center text-emerald-600 font-medium">
                              Ready
                            </span>
                          ) : (
                            <div className="flex flex-col gap-1 text-red-600 text-[11px] whitespace-normal min-w-[220px]">
                              {r.errors.map((err, errIdx) => (
                                <span key={errIdx} className="leading-tight">
                                  {err}
                                </span>
                              ))}
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="h-12 w-12 rounded-full bg-gray-50 flex items-center justify-center mb-3">
                <Upload className="text-gray-400" size={24} />
              </div>
              <p className="text-sm font-medium text-gray-900">No data to display</p>
              <p className="text-sm text-gray-500 mt-1">
                Download the template and upload the completed Excel file to preview assignments.
              </p>
            </div>
          )}
        </div>

        {/* Actions / Footer */}
        <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex justify-end gap-3 shrink-0 rounded-b-xl">
          <button
            type="button"
            onClick={resetAndClose}
            disabled={importing}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleImport}
            disabled={importing || validRows.length === 0}
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center min-w-[120px]"
          >
            {importing ? 'Importing...' : `Import ${validRows.length} valid row(s)`}
          </button>
        </div>
      </div>
    </div>
  );
}